import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Search,
  Button,
  Tag,
  Pagination,
  Loading,
  InlineNotification,
  Breadcrumb,
  BreadcrumbItem,
} from '@carbon/react';
import { View, Share, Portfolio, User, Search as SearchIcon } from '@carbon/icons-react';

interface PortfolioItem {
  userId: string; // Backend uses userId instead of user_id
  user_id?: string; // Fallback for user_id
  username: string;
  displayName: string; // Backend uses displayName instead of title
  title?: string; // Fallback for title
  bio: string; // Backend uses bio instead of description
  description?: string; // Fallback for description
  skills: string[]; // Backend uses skills instead of tags
  tags?: string[]; // Fallback for tags
  stats: {
    projects: number; // Backend uses stats.projects instead of item_count
    views: number;
    likes: number;
  };
  item_count?: number; // Fallback for item_count
  featuredProject?: {
    title: string;
    thumbnailUrl: string;
  };
  profileImageUrl?: string; // Backend uses profileImageUrl instead of profile_image
  profile_image?: string; // Fallback for profile_image
  location?: string;
  updated_at?: string;
}

const PortfolioDiscovery: React.FC = () => {
  const [portfolios, setPortfolios] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredPortfolios, setFilteredPortfolios] = useState<PortfolioItem[]>([]);

  const itemsPerPage = 12;

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch('/api/v1/portfolios/public');

        if (!response.ok) {
          throw new Error('Failed to fetch portfolios');
        }

        const data = await response.json();
        const portfolioList = data.portfolios || data.data || [];

        setPortfolios(portfolioList);
        setFilteredPortfolios(portfolioList);
        setTotalPages(Math.ceil(portfolioList.length / itemsPerPage));
      } catch (err: any) {
        console.error('Error fetching portfolios:', err);
        setError(err.message || 'Failed to load portfolios');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, []);

  useEffect(() => {
    // Filter portfolios based on search term
    if (!searchTerm.trim()) {
      setFilteredPortfolios(portfolios);
    } else {
      const filtered = portfolios.filter(
        portfolio =>
          (portfolio.displayName || portfolio.title)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (portfolio.bio || portfolio.description)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          portfolio.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (portfolio.skills || portfolio.tags)?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPortfolios(filtered);
    }

    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredPortfolios.length / itemsPerPage));
  }, [searchTerm, portfolios]);

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPortfolios.slice(startIndex, endIndex);
  };

  const handlePortfolioView = (portfolio: PortfolioItem) => {
    // Navigate to portfolio using username or userId
    const portfolioUrl = portfolio.username
      ? `/portfolio/${portfolio.username}`
      : `/portfolio/${portfolio.userId || portfolio.user_id}`;
    window.open(portfolioUrl, '_blank');
  };

  const handleShare = (portfolio: PortfolioItem) => {
    const portfolioUrl = portfolio.username
      ? `${window.location.origin}/portfolio/${portfolio.username}`
      : `${window.location.origin}/portfolio/${portfolio.userId || portfolio.user_id}`;

    if (navigator.share) {
      navigator.share({
        title: portfolio.displayName || portfolio.title,
        text: portfolio.bio || portfolio.description,
        url: portfolioUrl,
      });
    } else {
      navigator.clipboard.writeText(portfolioUrl);
      // Could add a toast notification here
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Recently updated';
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading portfolio discovery..." />
      </div>
    );
  }

  return (
    <Grid fullWidth>
      <Column lg={16} md={8} sm={4}>
        <Breadcrumb>
          <BreadcrumbItem href="/">Home</BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>Portfolio Discovery</BreadcrumbItem>
        </Breadcrumb>
      </Column>

      <Column lg={16} md={8} sm={4}>
        <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
          <h1
            style={{
              fontSize: 'var(--cds-expressive-heading-05-font-size)',
              fontWeight: 'var(--cds-expressive-heading-05-font-weight)',
              lineHeight: 'var(--cds-expressive-heading-05-line-height)',
              marginBottom: 'var(--cds-spacing-05)',
            }}
          >
            Discover Creative Portfolios
          </h1>
          <p
            style={{
              fontSize: 'var(--cds-body-01-font-size)',
              color: 'var(--cds-text-02)',
              marginBottom: 'var(--cds-spacing-07)',
            }}
          >
            Explore amazing creative work from our community of creators, designers, and innovators.
          </p>
        </div>
      </Column>

      <Column lg={16} md={8} sm={4}>
        <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
          <Search
            id="portfolio-search"
            labelText="Search portfolios"
            placeholder="Search by title, creator, or tags..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            renderIcon={SearchIcon}
            size="lg"
          />
        </div>
      </Column>

      {error && (
        <Column lg={16} md={8} sm={4}>
          <InlineNotification
            kind="error"
            title="Error loading portfolios"
            subtitle={error}
            lowContrast
            style={{ marginBottom: 'var(--cds-spacing-07)' }}
          />
        </Column>
      )}

      <Column lg={16} md={8} sm={4}>
        <div style={{ marginBottom: 'var(--cds-spacing-05)' }}>
          <p style={{ color: 'var(--cds-text-02)' }}>
            Showing {filteredPortfolios.length} portfolios
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
      </Column>

      {getCurrentPageItems().length === 0 ? (
        <Column lg={16} md={8} sm={4}>
          <Tile>
            <div style={{ textAlign: 'center', padding: 'var(--cds-spacing-07)' }}>
              <Portfolio size={48} style={{ marginBottom: 'var(--cds-spacing-05)' }} />
              <h3>No portfolios found</h3>
              <p style={{ color: 'var(--cds-text-02)' }}>
                {searchTerm
                  ? 'Try adjusting your search terms or browse all portfolios.'
                  : 'Be the first to create and share your portfolio!'}
              </p>
              {searchTerm && (
                <Button
                  kind="tertiary"
                  onClick={() => setSearchTerm('')}
                  style={{ marginTop: 'var(--cds-spacing-05)' }}
                >
                  Clear search
                </Button>
              )}
            </div>
          </Tile>
        </Column>
      ) : (
        <>
          {getCurrentPageItems().map(portfolio => (
            <Column lg={4} md={4} sm={4} key={portfolio.userId || portfolio.user_id}>
              <Tile style={{ marginBottom: 'var(--cds-spacing-05)', height: '100%', padding: 'var(--cds-spacing-05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--cds-spacing-03)' }}>
                  <User size={20} style={{ marginRight: 'var(--cds-spacing-03)' }} />
                  <span style={{ fontSize: 'var(--cds-body-compact-01-font-size)', color: 'var(--cds-text-02)' }}>
                    {portfolio.username || `User ${(portfolio.userId || portfolio.user_id || '').slice(0, 8)}`}
                  </span>
                </div>
                <h4
                  style={{
                    fontSize: 'var(--cds-productive-heading-04-font-size)',
                    fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                    marginBottom: 'var(--cds-spacing-03)',
                  }}
                >
                  {portfolio.displayName || portfolio.title || 'Creative Portfolio'}
                </h4>
                <p
                  style={{
                    fontSize: 'var(--cds-body-01-font-size)',
                    color: 'var(--cds-text-02)',
                    lineHeight: '1.4',
                    marginBottom: 'var(--cds-spacing-05)',
                  }}
                >
                  {portfolio.bio ||
                    portfolio.description ||
                    'Explore this creative portfolio showcasing innovative work.'}
                </p>
                {(portfolio.skills || portfolio.tags) && (portfolio.skills || portfolio.tags)!.length > 0 && (
                  <div style={{ marginBottom: 'var(--cds-spacing-05)' }}>
                    {(portfolio.skills || portfolio.tags)!.slice(0, 3).map((tag, index) => (
                      <Tag key={index} type="blue" size="sm" style={{ marginRight: 'var(--cds-spacing-03)' }}>
                        {tag}
                      </Tag>
                    ))}
                    {(portfolio.skills || portfolio.tags)!.length > 3 && (
                      <Tag type="gray" size="sm">
                        +{(portfolio.skills || portfolio.tags)!.length - 3} more
                      </Tag>
                    )}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--cds-spacing-05)',
                  }}
                >
                  <span style={{ fontSize: 'var(--cds-caption-01-font-size)', color: 'var(--cds-text-02)' }}>
                    {portfolio.stats?.projects || portfolio.item_count || 0} items • {portfolio.stats?.views || 0} views
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 'var(--cds-spacing-03)' }}>
                  <Button kind="primary" size="sm" renderIcon={View} onClick={() => handlePortfolioView(portfolio)}>
                    View Portfolio
                  </Button>
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Share}
                    onClick={() => handleShare(portfolio)}
                    hasIconOnly
                    iconDescription="Share portfolio"
                  />
                </div>
              </Tile>
            </Column>
          ))}
        </>
      )}

      {totalPages > 1 && (
        <Column lg={16} md={8} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--cds-spacing-07)' }}>
            <Pagination
              page={currentPage}
              totalItems={filteredPortfolios.length}
              pageSize={itemsPerPage}
              pageSizes={[itemsPerPage]}
              onChange={({ page }) => setCurrentPage(page)}
            />
          </div>
        </Column>
      )}
    </Grid>
  );
};

export default PortfolioDiscovery;
